import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import Pagination from "./Pagination";

const ImageModal = ({ isOpen, onClose, imageUrl, fileName }) => {
  if (!isOpen) return null;

  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="fixed inset-0 bg-transparent" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full h-full flex flex-col items-center justify-center">
        <div className="absolute top-4 right-4 flex space-x-4 bg-black bg-opacity-50 rounded-full p-1">
          <button onClick={handleDownload} className="text-white hover:text-gray-300 p-2 transition-colors" title="Download image" aria-label="Download image">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <button onClick={onClose} className="text-white hover:text-gray-300 p-2 transition-colors" aria-label="Close modal">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="bg-white p-1 rounded-lg shadow-lg">
          <img src={imageUrl} alt={fileName || "Full screen image"} className="max-w-full max-h-[90vh] object-contain rounded" />
          <div className="p-2 text-center text-sm text-gray-700 truncate">{fileName}</div>
        </div>
      </div>
    </div>
  );
};

const AttachmentPreview = ({ attachment }) => {
  const [expanded, setExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleDownload = async () => {
    try {
      const response = await fetch(attachment.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return '📷';
      case 'pdf': return '📄';
      case 'document': return '📝';
      case 'video': return '🎥';
      case 'audio': return '🎵';
      case 'zip': case 'archive': return '🗜️';
      case 'spreadsheet': return '📊';
      case 'presentation': return '📽️';
      default: return '📎';
    }
  };

  const getFileTypeLabel = (fileName) => {
    if (!fileName) return '';
    const extension = fileName.split('.').pop().toLowerCase();
    return extension ? `.${extension}` : '';
  };

  return (
    <>
      <div className="border rounded p-2 mt-2 bg-gray-50 hover:shadow-md transition-shadow">
        {attachment.fileType === 'image' ? (
          <div className={`${expanded ? 'w-full' : 'w-40 h-40'} overflow-hidden transition-all duration-300 relative`}>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <svg className="animate-spin w-8 h-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
            <img
              src={attachment.url}
              alt={attachment.fileName || "Image attachment"}
              className={`object-contain cursor-pointer hover:opacity-90 transition-opacity ${expanded ? 'w-full max-h-96' : 'w-40 h-40'} ${imageLoaded ? 'block' : 'hidden'}`}
              onClick={() => setIsModalOpen(true)}
              onLoad={() => setImageLoaded(true)}
            />
            <div className="mt-2 flex justify-between items-center">
              <button onClick={() => setExpanded(!expanded)} className="text-sm text-blue-600 hover:text-blue-800 transition-colors">
                {expanded ? "Show less" : "Expand preview"}
              </button>
              <button onClick={handleDownload} className="text-sm text-blue-600 hover:text-blue-800 flex items-center transition-colors">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{getFileIcon(attachment.fileType)}</span>
              <div className="flex flex-col">
                <span className="text-sm font-medium truncate max-w-xs">{attachment.fileName}</span>
                <span className="text-xs text-gray-500">{getFileTypeLabel(attachment.fileName)}</span>
              </div>
            </div>
            <button onClick={handleDownload} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 flex items-center text-sm py-1 px-2 rounded transition-colors">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          </div>
        )}
      </div>
      <ImageModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} imageUrl={attachment.url} fileName={attachment.fileName} />
    </>
  );
};

const AnnouncementList = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedAnnouncements, setExpandedAnnouncements] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [sortOrder]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = { paginate: "true", page, limit, sort: sortOrder };
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await axiosInstance.get(API_ENDPOINTS.announcements, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setAnnouncements(response.data.data?.announcements ?? []);
      setTotal(response.data.data?.pagination?.total ?? 0);
      setError("");
    } catch (err) {
      if (err.response?.status === 403) {
        setAnnouncements([]);
        setError("");
        return;
      }
      console.error("Error fetching announcements:", err);
      setError("Failed to fetch announcements. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortOrder, debouncedSearch]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const toggleExpand = (id) => {
    setExpandedAnnouncements(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto py-6">
      <div className="sticky top-0 z-10 bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-64">
            <input
              type="search"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              aria-label="Search announcements"
            />
            <div className="absolute left-3 top-2.5 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="border rounded-lg py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              aria-label="Sort order"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <button
              onClick={() => fetchAnnouncements()}
              className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition-colors"
              aria-label="Refresh announcements"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-sm">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12 bg-gray-50 rounded-lg">
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2 text-gray-600">Loading announcements...</span>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            {total} announcement{total !== 1 ? 's' : ''}
            {debouncedSearch && ` matching "${debouncedSearch}"`}
          </p>

          {announcements.length === 0 && !error ? (
            <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <p className="text-gray-500 mt-2">
                {debouncedSearch ? "No announcements matching your search." : "No announcements available."}
              </p>
              {debouncedSearch && (
                <button onClick={() => setSearchTerm("")} className="mt-4 text-blue-600 hover:text-blue-800 font-medium hover:underline">
                  Clear search
                </button>
              )}
            </div>
          ) : (
            announcements.map((announcement) => {
              const isExpanded = expandedAnnouncements.has(announcement._id);
              const hasAttachments = announcement.attachments && announcement.attachments.length > 0;
              const formattedDate = new Date(announcement.createdAt).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
              });

              return (
                <div key={announcement._id} className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <h3 className="text-xl font-semibold text-gray-800">{announcement.title}</h3>
                    <span className="text-sm text-gray-500 whitespace-nowrap">{formattedDate}</span>
                  </div>

                  <div className={`mt-3 text-gray-700 ${isExpanded ? '' : 'line-clamp-3'}`}>
                    {announcement.content}
                  </div>

                  {announcement.content.length > 150 && (
                    <button
                      onClick={() => toggleExpand(announcement._id)}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline focus:outline-none"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? "Show less" : "Show more"}
                    </button>
                  )}

                  {hasAttachments && (
                    <div className={`mt-4 ${isExpanded ? 'animate-fadeIn' : 'hidden'}`}>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Attachments ({announcement.attachments.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {announcement.attachments.map((attachment, index) => (
                          <AttachmentPreview key={index} attachment={attachment} />
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAttachments && !isExpanded && (
                    <button
                      onClick={() => toggleExpand(announcement._id)}
                      className="mt-3 flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      View {announcement.attachments.length} attachment{announcement.attachments.length !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              );
            })
          )}

          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={limit}
            onPageChange={setPage}
            onItemsPerPageChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
            itemLabel="announcements"
          />
        </>
      )}

      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default AnnouncementList;
